import type { CreatorView } from '@gardener/protocol';
import { createResource, createSignal, For, Match, onMount, Show, Switch } from 'solid-js';
import { call } from './api';
import { go, route } from './nav';
import {
  ask,
  Copy,
  EmptyState,
  Icon,
  IconButton,
  LoadingLine,
  optimistic,
  SETTINGS_INPUT,
  SettingsCard,
  SettingsItem,
  toast,
  Well,
} from './ui';

/**
 * Verified creators (ADR-0128): a streamer links their Twitch in the game (Link Twitch), then is approved here by that
 * Twitch login: the purple check, the creator items and their `/c/<login>` link. Revoking takes all of it back. Both show
 * at once and are taken back if the server refuses (ADR-0154). Laid out as logdash's API keys page: a settings card to
 * add one, a well listing them.
 */
export function Creators() {
  const [list, { mutate }] = createResource(() => call<{ creators: CreatorView[] }>('GET', '/api/admin/creators'));
  const [twitch, setTwitch] = createSignal('');
  let field!: HTMLInputElement;
  onMount(() => {
    if (route().arg === 'new') field.focus();
  });
  // A resource read while it failed throws (and takes the app down): ask for its error first.
  const loaded = () => (list.error ? undefined : list());
  const creators = () => loaded()?.creators ?? [];

  async function approve(e: SubmitEvent) {
    e.preventDefault();
    const login = twitch().trim().toLowerCase();
    if (!login) return;
    // Who it is comes with the answer: until then, their Twitch login stands in.
    const draft: CreatorView = { id: '', name: login, twitch: login, creator: true, live: false };
    await optimistic(
      () => {
        const before = loaded();
        mutate({ creators: [draft, ...(before?.creators ?? [])] });
        setTwitch('');
        return () => {
          mutate(before);
          setTwitch(login);
        };
      },
      () => call<CreatorView>('POST', '/api/admin/creators', { twitch: login }),
      (made) => {
        mutate((l) => ({
          creators: [made, ...(l?.creators ?? []).filter((c) => c !== draft && c.id !== made.id)],
        }));
        toast(`${made.name} is a verified creator`);
      },
    );
  }
  async function revoke(c: CreatorView) {
    const sure = await ask({
      title: `Take ${c.name}'s creator check back?`,
      body: 'Their creator items go too, and their /c/ link stops working.',
      confirm: 'Revoke',
      danger: true,
    });
    if (sure === null) return;
    await optimistic(
      () => {
        const before = loaded();
        mutate({ creators: (before?.creators ?? []).filter((x) => x.id !== c.id) });
        return () => mutate(before);
      },
      () => call('DELETE', `/api/admin/creators/${encodeURIComponent(c.id)}`),
      () => toast(`${c.name} is no longer a creator`),
    );
  }

  return (
    <div class="@container flex w-full flex-col gap-2 p-2">
      {/* logdash's SettingsCard with one SettingsCardItem, as its Team card's invite row. */}
      <SettingsCard
        title="Approve a creator"
        description="Their account must have linked that Twitch first (Link Twitch, in the game)."
      >
        <SettingsItem>
          <form class="flex min-w-0 items-center gap-3" onSubmit={approve}>
            <span class="text-fg-muted w-16 shrink-0">Twitch</span>
            <label class={`${SETTINGS_INPUT} -my-1.5 max-w-80 min-w-0 flex-1`} data-size="sm" data-variant="filled">
              <Icon name="twitch" class="text-fg-muted size-3.5 shrink-0" />
              <input
                ref={field}
                value={twitch()}
                onInput={(e) => setTwitch(e.currentTarget.value)}
                placeholder="sprouty"
                aria-label="Twitch login"
                autocomplete="off"
                spellcheck={false}
              />
            </label>
            <button type="submit" class="ld-button -my-1.5" data-variant="primary" data-size="sm">
              Approve
            </button>
          </form>
        </SettingsItem>
      </SettingsCard>

      {/* logdash's Well, its rows as the API keys list's. */}
      <Well
        title="Verified creators"
        actions={
          <Show when={!list.loading && !list.error}>
            <span class="text-fg-muted px-3 text-xs tabular-nums">
              {creators().length} {creators().length === 1 ? 'creator' : 'creators'}
            </span>
          </Show>
        }
      >
        <Switch>
          <Match when={list.loading && !creators().length}>
            <div class="px-3 py-2.5">
              <LoadingLine label="Loading creators" />
            </div>
          </Match>
          <Match when={list.error}>
            {/* logdash's failed list (`TeamManagementCard`): a muted line after a danger sign. */}
            <div class="text-fg-muted flex items-center gap-2 px-3 py-2.5 text-[13px]" role="alert">
              <Icon name="danger" class="size-4 shrink-0" />
              {(list.error as Error).message}
            </div>
          </Match>
          <Match when={!creators().length}>
            <EmptyState
              centered
              title="No creators yet"
              description="Approve a streamer by their Twitch login once they have linked it in the game."
            />
          </Match>
          <Match when={creators().length}>
            <ul class="edge-between flex flex-col">
              <For each={creators()}>
                {(c) => (
                  <li
                    class="focus-visible:outline-brand flex items-center gap-4 rounded-lg px-3 py-3 focus-visible:outline-2 focus-visible:-outline-offset-2"
                    classList={{ 'opacity-60': !c.id, 'hover:bg-surface-25-hover-bg cursor-pointer': !!c.id }}
                    tabIndex={c.id ? 0 : undefined}
                    onClick={() => c.id && go('players', c.id)}
                    onKeyDown={(e) => e.key === 'Enter' && e.target === e.currentTarget && c.id && go('players', c.id)}
                  >
                    <div class="flex min-w-0 flex-1 flex-col gap-1">
                      <div class="flex min-w-0 items-center gap-2">
                        <span class="truncate text-sm font-medium">{c.name}</span>
                        <Show when={c.live}>
                          <span class="ld-badge shrink-0 gap-1" data-variant="error" data-size="sm">
                            <span class="bg-error size-1.5 rounded-full" aria-hidden="true" />
                            Live
                          </span>
                        </Show>
                      </div>
                      <span class="text-fg-muted flex min-w-0 flex-wrap gap-x-2 text-xs">
                        <a
                          class="hover:text-fg-default transition-ink inline-flex items-center gap-1"
                          href={`https://twitch.tv/${c.twitch}`}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Icon name="twitch" class="size-3 shrink-0" />
                          {c.twitch}
                        </a>
                        <span aria-hidden="true">·</span>
                        <span class="font-mono">gaws.io/c/{c.twitch}</span>
                      </span>
                    </div>
                    <div class="-mr-1.5 flex shrink-0 items-center gap-1">
                      <Copy text={`https://gaws.io/c/${c.twitch}`} label="Copy the link" well />
                      <IconButton
                        label={`Revoke ${c.name}`}
                        danger
                        well
                        disabled={!c.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          void revoke(c);
                        }}
                      >
                        <Icon name="trash" class="size-4" />
                      </IconButton>
                    </div>
                  </li>
                )}
              </For>
            </ul>
          </Match>
        </Switch>
      </Well>
    </div>
  );
}
